import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import styled from 'styled-components'

import { ArrowReplyIcon, MinusIcon } from '@blade/ui-kit'

import { CommentReaction } from '../comment-reaction/comment-reaction'

import { CommentItemBodyContent } from './comment-item-body-content'
import { CommentItemBodyEdit } from './comment-item-body-edit'
import { CommentItemBodyReply } from './comment-item-body-reply'
import { COMMENT_ITEM_AVATAR_SIZE } from './consts'

import type { CommentId } from '../../model/comment-id'
import type { Comment } from '@root/entities/comment'
import type { CommentsModel } from '@root/features/comment/atoms'

type CommentItemBodyProps = {
  comment: Comment.Comment
  highlightId: CommentId | undefined
  topCollapseHidden: boolean

  setCollapse: Dispatch<SetStateAction<CommentId | undefined>>
  setHighlight: Dispatch<SetStateAction<CommentId | undefined>>
  setDepth: Dispatch<SetStateAction<number | undefined>>
  model: CommentsModel
}

export const CommentItemBody = ({
  comment,
  highlightId,
  topCollapseHidden,
  setCollapse,
  setHighlight,
  setDepth,
  model,
}: CommentItemBodyProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const replies = comment.replies
  const repliesTotal = replies.length

  const [editCommentId] = useAtom(model.editCommentIdAtom)
  const [replyCommentId, setReplyCommentId] = useAtom(model.replyCommentIdAtom)
  const toggleReactionCommentAction = useAction(
    model.toggleReactionCommentAction,
  )

  const isReplyOpen = replyCommentId === comment.id
  const isEditOpen = editCommentId === comment.id
  const isHighlighted = highlightId === comment.id
  const noReplies = repliesTotal === 0

  useEffect(() => {
    if (isReplyOpen && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isReplyOpen])

  const renderTextOrEdit = () => {
    if (isEditOpen) {
      return <CommentItemBodyEdit comment={comment} model={model} />
    }

    return <CommentItemBodyContent comment={comment} />
  }

  return (
    <Row>
      {noReplies ? (
        <Blank />
      ) : (
        <Highlight
          onMouseEnter={() => {
            setHighlight(comment.id)
            setCollapse(comment.id)
          }}
          onMouseLeave={() => {
            setHighlight(undefined)
            setCollapse(undefined)
          }}
          onClick={() => setDepth(-1)}
        >
          {topCollapseHidden && <ButtonCollapse />}
          <Line $highlight={isHighlighted} />
          <CornerArrowIcon size={11} $highlight={isHighlighted} />
        </Highlight>
      )}
      <Group>
        {renderTextOrEdit()}
        <CommentReaction
          comment={comment}
          onReply={() => setReplyCommentId(comment.id)}
          onReaction={(type) =>
            toggleReactionCommentAction({
              pairAddress: comment.pairAddress,
              commentId: comment.id,
              reaction: type,
            })
          }
        />
        {isReplyOpen ? (
          <>
            <CommentItemBodyReply comment={comment} model={model} />
            <div ref={ref} />
          </>
        ) : undefined}
      </Group>
    </Row>
  )
}

const Row = styled.div`
  align-items: center;

  gap: 10px;

  display: grid;
  grid-template-columns: 20px 1fr;
`

const Group = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  justify-items: start;

  margin-block: 8px 4px;
`

const Line = styled.div<{ $highlight: boolean }>`
  background: ${(props) =>
    props.$highlight
      ? LineBackgroundUrl('white')
      : LineBackgroundUrl('rgb(96, 96, 96)')};

  position: relative;
  cursor: pointer;

  width: 20px;
  z-index: -1;
  background-position: 10px;
  transition: background 0.1s ease-in-out;
`

const LineBackgroundUrl = (color: string): string => {
  const svg = `<svg height="17" width="17" xmlns="http://www.w3.org/2000/svg"><line x1="0" y1="0" x2="0" y2="20" stroke-width="2" stroke="${color}" />`

  return `url('data:image/svg+xml,${svg}</svg>')`
}

const Highlight = styled.div`
  position: relative;
  display: grid;
  cursor: pointer;

  margin-top: 5px;
  height: 100%;
  width: 20px;
`

const ButtonCollapse = styled(MinusIcon)`
  background: var(--background-12);
  color: var(--text-8);

  cursor: pointer;
  position: absolute;
  display: grid;
  justify-content: center;
  align-items: center;

  width: 17px;
  height: 17px;
  left: 2px;
  top: 50%;
  padding: 3px;
  z-index: 1;
  border-radius: 50%;

  &:hover {
    opacity: 1;
  }
`

const CornerArrowIcon = styled(ArrowReplyIcon)<{
  $highlight: boolean
}>`
  color: ${(props) =>
    props.$highlight ? 'var(--background-12)' : 'var(--background-8)'};

  position: absolute;

  /* left: ${COMMENT_ITEM_AVATAR_SIZE / 2}px; */
  bottom: -12px;
  z-index: -2;

  left: 0;
  transform: translateX(10px);

  transition: color 0.1s ease-in-out;
`

const Blank = styled.aside`
  width: ${COMMENT_ITEM_AVATAR_SIZE}px;
`
